import { Button, ButtonVariant, CharacterCardVariant, Spinner } from "@next-platform/client-common-spicy-ui";
import { memo, useMemo } from "react";

import { Col, Row } from "@/components";
import type { CharacterCardProps } from "@/components/Character/CharacterCard/types";
import CharacterCard from "@/components/Character/CharacterCard/variants";
import Trans from "@/components/Trans/Trans";
import type { HitConfig } from "@/types/hits";

import RulebookProfileSectionHeading from "./RulebookProfileSectionHeading";
import useRulebookProfileBots from "./useRulebookProfileBots";

const COLLAPSED_BOT_CARD_WIDTH = 160;

type RulebookBotsSectionProps = {
  rulebookId: string;
};

type CardCharacterInfo = CharacterCardProps["characterInfo"];

/** The loose hit-config fields `CharacterCard` actually reads, narrowed to its prop contract. */
const toCharacterInfo = (config: HitConfig): CardCharacterInfo => ({
  characterId: config.characterId ?? "",
  characterName: config.characterName ?? "",
  characterCreatorUserName: config.characterCreatorUserName ?? "",
  characterTitle: config.characterTitle ?? "",
  characterTags: config.characterTags,
  characterMessageNumber: config.characterMessageNumber ?? "",
  characterRatingScore: config.characterRatingScore,
  characterAvatarUrl: config.characterAvatarUrl ?? "",
  characterDefinitionVisible: config.characterDefinitionVisible,
  characterLikeLabel: config.characterLikeLabel,
  characterInfoLabel: config.characterInfoLabel,
  characterTokensCount: config.characterTokensCount,
  characterImageGen: config.characterImageGen,
  creatorPagePath: config.creatorPagePath,
  chatPagePath: config.chatPagePath,
  chatbotProfilePagePath: config.chatbotProfilePagePath,
  characterAvatarBlur: config.characterAvatarBlur,
  characterImageGenLabel: config.characterImageGenLabel,
  hasVoice: config.hasVoice,
  variantLabel: config.variantLabel,
  lorebookAttached: config.lorebookAttached,
  lorebookLabel: config.lorebookLabel,
});

export type RulebookBotsSectionViewProps = ReturnType<typeof useRulebookProfileBots>;

/** Presentational bots section shared by the live page and visual fixtures. */
export const RulebookBotsSectionView = ({
  configs,
  found,
  isLoading,
  isHidden,
  isExpanded,
  isLoadingMore,
  hasMore,
  onSeeAll,
  onLoadMore,
}: RulebookBotsSectionViewProps) => {
  const seeAllValues = useMemo(() => ({ count: found }), [found]);
  const seeAllButton =
    !isExpanded && found > configs.length ? (
      <Button
        variant={ButtonVariant.TEXT}
        className="h-auto min-w-0 p-0 text-[13px] font-semibold"
        onClick={onSeeAll}
        data-testid="RulebookProfile-Bots-SeeAll"
      >
        <Trans i18nKey="rulebook:profile.bots.seeAll" values={seeAllValues} />
      </Button>
    ) : null;

  if (isHidden || (!isLoading && configs.length === 0)) {
    return null;
  }

  return (
    <Col className="gap-3" dataTestId="RulebookProfile-Bots">
      <RulebookProfileSectionHeading title={<Trans i18nKey="rulebook:profile.bots.title" />} action={seeAllButton} />
      {isLoading ? (
        <div className="flex justify-center py-4" data-testid="RulebookProfile-Bots-Loading">
          <Spinner />
        </div>
      ) : (
        <>
          <Row className={isExpanded ? "flex-wrap gap-2" : "flex-nowrap gap-3 overflow-x-auto pb-1"} data-testid="RulebookProfile-Bots-Cards">
            {configs.map((config) => (
              <div key={config.uniqueKey ?? config.id} className={isExpanded ? undefined : "w-[160px] flex-none"}>
                <CharacterCard
                  characterInfo={toCharacterInfo(config)}
                  variant={config.characterCardVariant ?? CharacterCardVariant.DEFAULT}
                  characterCardActionsHandler={config.characterCardActionsHandler}
                  characterCardActionsState={config.characterActionsState}
                  dropdownMenuActions={config.dropdownActions}
                  maxWidth={isExpanded ? undefined : COLLAPSED_BOT_CARD_WIDTH}
                  data-testid={`RulebookProfile-Bot-${config.id}`}
                />
              </div>
            ))}
          </Row>
          {isExpanded && hasMore ? (
            <Button
              variant={ButtonVariant.TEXT}
              onClick={onLoadMore}
              disabled={isLoadingMore}
              loading={isLoadingMore}
              data-testid="RulebookProfile-Bots-LoadMore"
            >
              <Trans i18nKey="rulebook:profile.bots.loadMore" />
            </Button>
          ) : null}
        </>
      )}
    </Col>
  );
};

/**
 * "Bots using it" — public characters Typesense hits filtered on `rulebook_id`, rendered with the
 * same CharacterCard the explore grid uses. Hidden entirely on error or when nothing is attached.
 */
const RulebookBotsSection = ({ rulebookId }: RulebookBotsSectionProps) => {
  const profileBots = useRulebookProfileBots({ rulebookId });
  return <RulebookBotsSectionView {...profileBots} />;
};

export default memo(RulebookBotsSection);
